"use client";

import { c as _c } from "react/compiler-runtime";
import type { ErrorEvent, EventHint } from "@sentry/nextjs";
import { init, isInitialized, replayIntegration } from "@sentry/nextjs";
import { useEffect } from "react";
import { SENTRY_DSN } from "@/constants";

/**
 * List of patterns and error types that are commonly caused by third-party scripts or browser extensions, and are not actionable for our app.
 * This includes known issues from iOS WKWebView, OneTrust cookie consent SDK, Webtrends analytics, Firefox reader mode, IAB consent framework, and common network errors.
 * By filtering these out in beforeSend, we can reduce noise in Sentry and focus on errors that we can actually fix in our codebase.
 * See https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#beforesend for more on event filtering.
 */
const THIRD_PARTY_ERROR_PATTERNS = [
// iOS WKWebView bridge — not actionable
"window.webkit.messageHandlers",
// OneTrust cookie consent SDK
"otBannerSdk", "otSDKStub.js", "cdn.cookielaw.org", "DomainData", "bannerGroup.html",
// Webtrends analytics — iOS property descriptor bug
"filterEventObjectForWT",
// Firefox reader mode internals
"window.__firefox__",
// IAB consent framework
"iabjs_unified_bridge",
// Browser API not available in all contexts
"getHighEntropyValues is not a function",
// Browser extension conflicts
"Cannot redefine property: timeStamp",
// Perfalytics - AbortError triggered by iOS Facebook browser on media landers
"api.perfalytics.com",
// LogRocket
"lrkt-in.com"];

/**
 * Some errors have very generic messages that can be caused by a wide range of issues, many of which are outside our control (e.g. network errors, browser extensions).
 * We can filter these out based on their exact message to further reduce noise, while being careful not to filter out legitimate errors from our codebase.
 */
const THIRD_PARTY_ERROR_TYPES = ["Failed to fetch", "Load failed", "network error", "Importing a module script failed", "Error in input stream", "AbortError: The operation was aborted."];
const TRACES_SAMPLE_RATE = 0.1;

// Grafana's scripted prod check replays the same flow every few minutes; its pageloads and web
// vitals would otherwise make up roughly half of the field CLS/INP samples. Errors are still sent.
const SYNTHETIC_USER_AGENT_PATTERN = "GrafanaSyntheticMonitoring";
const isSyntheticMonitor = () => globalThis.navigator?.userAgent?.includes(SYNTHETIC_USER_AGENT_PATTERN) ?? false;
function shouldFilterEvent(event: ErrorEvent, hint: EventHint): boolean {
  const error = hint.originalException;
  let message = "";
  if (typeof error === "string") {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  }
  if (THIRD_PARTY_ERROR_PATTERNS.some(pattern => message.includes(pattern) || event.exception?.values?.some(v => v.value?.includes(pattern)))) {
    return true;
  }
  if (THIRD_PARTY_ERROR_TYPES.some(type => message === type || event.exception?.values?.some(v => v.value === type))) {
    return true;
  }

  // Filter errors where all stack frames are from third-party scripts
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (frames?.length) {
    const allThirdParty = frames.every(frame => !frame.filename || frame.filename.includes("otBannerSdk") || frame.filename.includes("otSDKStub") || frame.filename.includes("cookielaw.org") || frame.filename.includes("LogRocket") || frame.filename === "<anonymous>" || frame.filename === "[native code]");
    if (allThirdParty) return true;
  }

  // DOM Events (e.g. CustomEvent from Invoca call tracking) thrown as promise rejections
  // are always from third-party scripts — no app code should ever reject with a DOM Event.
  if (typeof Event !== "undefined" && hint.originalException instanceof Event) {
    return true;
  }
  return false;
}
const initializeSentryClient = (environment: string) => {
  if (isInitialized()) {
    return;
  }
  if (environment !== "prod" && environment !== "stage") {
    return;
  }
  const localEnv = environment === "prod" ? "production" : environment;
  try {
    init({
      dsn: SENTRY_DSN,
      environment: localEnv,
      integrations: [replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      })],
      tracesSampler: ({
        inheritOrSampleWith
      }) => isSyntheticMonitor() ? 0 : inheritOrSampleWith(TRACES_SAMPLE_RATE),
      attachStacktrace: false,
      replaysSessionSampleRate: localEnv === "production" ? 0.05 : 0.01,
      replaysOnErrorSampleRate: localEnv === "production" ? 0.05 : 0.01,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: {
            deny: ["forwarded", "-ip", "remote-", "via", "-user"]
          },
          response: {
            deny: ["forwarded", "-ip", "remote-", "via", "-user"]
          }
        },
        httpBodies: [],
        urlQueryParams: {
          deny: ["forwarded", "-ip", "remote-", "via", "-user"]
        },
        genAI: {
          inputs: false,
          outputs: false
        },
        databaseQueryData: false,
        graphQL: {
          document: false,
          variables: false
        }
      },
      beforeSend(event, hint) {
        // Filter CMS preview-mode params (aspen-marketing pattern; harmless in aspen-scheduler).
        const params = new URLSearchParams(globalThis.location.search);
        if (params.get("isPreview") === "true" || params.get("expEditorMode") === "true" || shouldFilterEvent(event, hint)) {
          return null;
        }
        return event;
      }
    });
  } catch {
    // Sentry init can fail in certain browser environments (e.g. iOS WKWebView)
    // where performance APIs return unexpected values during integration setup.
    // Silently catch to prevent the app from crashing.
  }
};
const SentryInit = t0 => {
  const $ = _c(3);
  const {
    environment
  } = t0;
  let t1;
  let t2;
  if ($[0] !== environment) {
    t1 = () => {
      if (!environment) {
        return;
      }
      initializeSentryClient(environment);
    };
    t2 = [environment];
    $[0] = environment;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
};
export default SentryInit;